import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  CreateGameRequest,
  Game,
  GameDialogDefaultValues,
  GameDialogMode,
  GameFormData,
  GameFormErrors,
  UpdateGameRequest,
} from '../../../../types/features/games';
import type { SimpleState } from '../../../../types/locations';
import { getGameImageSource } from '../../../../utils/normalizers/gameImageUrl';
import {
  buildGameRequestPayload,
  createEmptyGameFormData,
  createGameFormDataFromGame,
  getGameCountryDisplayLabel,
  getGameStateDisplayLabel,
  updateGameFormData,
  validateGameForm,
  validateSelectedImage,
} from '../mappers/gameDialogMappers';

interface UseGameDialogLogicParams {
  game?: Game | null;
  mode: GameDialogMode;
  loading: boolean;
  defaultValues?: GameDialogDefaultValues;
  onClose: () => void;
  onSubmit: (data: CreateGameRequest | UpdateGameRequest) => Promise<boolean>;
  states: SimpleState[];
}

export const useGameDialogLogic = ({
  game,
  mode,
  loading,
  defaultValues,
  onClose,
  onSubmit,
  states,
}: UseGameDialogLogicParams) => {
  const [formData, setFormData] = useState<GameFormData>(() => createEmptyGameFormData(defaultValues));
  const [errors, setErrors] = useState<GameFormErrors>({});
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState('');
  const [existingImageUrl, setExistingImageUrl] = useState('');

  useEffect(() => {
    if (game && mode === 'update') {
      setFormData(createGameFormDataFromGame(game));
      setExistingImageUrl(getGameImageSource(game));
    } else {
      setFormData(createEmptyGameFormData(defaultValues));
      setExistingImageUrl('');
    }

    setSelectedImage(null);
    setImagePreview('');
    setErrors({});
  }, [defaultValues, game, mode]);

  useEffect(() => () => {
    if (imagePreview) {
      URL.revokeObjectURL(imagePreview);
    }
  }, [imagePreview]);

  const countryDisplayLabel = useMemo(
    () => getGameCountryDisplayLabel(states, formData.country_code, defaultValues?.country_name),
    [defaultValues?.country_name, formData.country_code, states],
  );
  const stateDisplayLabel = useMemo(
    () => getGameStateDisplayLabel(states, formData.state_code, defaultValues?.state_name),
    [defaultValues?.state_name, formData.state_code, states],
  );

  const handleInputChange = useCallback((field: keyof GameFormData, value: string | boolean) => {
    setFormData((previousFormData) => updateGameFormData(previousFormData, field, value));

    setErrors((previousErrors) => {
      if (!previousErrors[field]) {
        return previousErrors;
      }

      return {
        ...previousErrors,
        [field]: '',
      };
    });
  }, []);

  const handleImageSelect = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;

    if (!file) {
      return;
    }

    const imageError = validateSelectedImage(file);

    if (imageError) {
      setErrors((previousErrors) => ({
        ...previousErrors,
        image: imageError,
      }));
      return;
    }

    const objectUrl = URL.createObjectURL(file);

    setSelectedImage(file);
    setImagePreview((previousPreview) => {
      if (previousPreview) {
        URL.revokeObjectURL(previousPreview);
      }

      return objectUrl;
    });
    setErrors((previousErrors) => ({
      ...previousErrors,
      image: '',
      image_url: '',
    }));
  }, []);

  const handleRemoveImage = useCallback(() => {
    setSelectedImage(null);
    setImagePreview((previousPreview) => {
      if (previousPreview) {
        URL.revokeObjectURL(previousPreview);
      }

      return '';
    });

    if (existingImageUrl) {
      setExistingImageUrl('');
      setFormData((previousFormData) => ({
        ...previousFormData,
        image_url: '',
      }));
    }
  }, [existingImageUrl]);

  const handleClose = useCallback(() => {
    if (!loading) {
      onClose();
    }
  }, [loading, onClose]);

  const handleSubmit = useCallback(async () => {
    const nextErrors = await validateGameForm(formData, selectedImage);
    setErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    const success = await onSubmit(buildGameRequestPayload(formData, selectedImage));

    if (success) {
      onClose();
    }
  }, [formData, onClose, onSubmit, selectedImage]);

  return {
    countryDisplayLabel,
    errors,
    existingImageUrl,
    formData,
    imagePreview,
    selectedImage,
    stateDisplayLabel,
    handleClose,
    handleImageSelect,
    handleInputChange,
    handleRemoveImage,
    handleSubmit,
  };
};
